import { FC, useCallback, useEffect, useState } from 'react'

import { Settings } from '@/components/layout/settings/layout'
import SettingsTitle from '@/components/layout/settings/title'
import { Badge } from '@/components/ui/badge'
import useList from '@/hooks/useList'
import { Admin, AdminRole } from '@/out/admin'
import { Invitation } from '@/out/invitation'
import InviteAdmin from '@/pages/settings/admin-management/Invite'
import { listAdmins, Request as AdminRequest } from '@/thunks/admins/list'
import { listInvitations, Request as InvitationRequest } from '@/thunks/invitations/list'
import fadeIn from '@/util/fadeIn'

import AdminRow from './AdminRow'

type AdminManagementItem = (Admin & { type: 'admin' }) | (Invitation & { type: 'invitation' })

const AdminManagement: FC = () => {
  const [admins, { wasLoaded: adminsLoaded, patch: patchAdmin, remove: removeAdmin }] = useList<Admin, AdminRequest>(
    listAdmins,
    {}
  )
  const [
    invitations,
    { wasLoaded: invitationsLoaded, add: addInvitation, patch: patchInvitation, remove: removeInvitation },
  ] = useList<Invitation, InvitationRequest>(listInvitations, {})

  const [allLoaded, setAllLoaded] = useState(false)

  useEffect(() => {
    if (adminsLoaded && invitationsLoaded) {
      setAllLoaded(true)
    }
  }, [adminsLoaded, invitationsLoaded])

  const handleInviteCreated = useCallback(
    (invite: Invitation) => {
      addInvitation(invite)
    },
    [addInvitation]
  )

  const handleRoleChange = useCallback(
    (id: string, role: AdminRole) => {
      const admin = admins?.find(a => a.id === id)
      const invitation = invitations?.find(i => i.id === id)

      if (admin) {
        patchAdmin({ id, role })
      } else if (invitation) {
        patchInvitation({ id, role })
      }
    },
    [admins, invitations, patchAdmin, patchInvitation]
  )

  if (!admins || !invitations) return null

  const adminData: AdminManagementItem[] = admins.map(admin => ({ ...admin, type: 'admin' as const }))
  const invitationData: AdminManagementItem[] = invitations.map(invitation => ({
    ...invitation,
    type: 'invitation' as const,
  }))

  return (
    <Settings data-testid="admin-management-page">
      <SettingsTitle title="Admin Management">
        <InviteAdmin key={invitations?.length} onCreate={handleInviteCreated} />
      </SettingsTitle>

      {invitationData.length > 0 && (
        <div className="pt-2">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-base font-medium">Pending Invitations</h3>
            <Badge variant="outline" className="text-xs">
              {invitationData.length}
            </Badge>
          </div>
          {invitationData.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">There are no pending invitations</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="pending-invitations-container">
              {invitationData.map((item, index) => (
                <div
                  key={item.id}
                  data-testid="pending-invitation-section"
                  data-invitation-id={item.id}
                  data-invitation-email={item.email}
                  data-invitation-index={index}
                >
                  <AdminRow item={item} onRoleChange={handleRoleChange} onRevoke={removeInvitation} />
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 mt-6">
            <h3 className="text-base font-medium">Active Admins</h3>
            <Badge variant="outline" className="text-xs">
              {adminData.length}
            </Badge>
          </div>
        </div>
      )}

      <div className={fadeIn(allLoaded)}>
        {adminData.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-muted-foreground">No active admins</p>
          </div>
        ) : (
          <div className="space-y-2">
            {adminData.map(item => (
              <AdminRow key={item.id} item={item} onRoleChange={handleRoleChange} onArchive={removeAdmin} />
            ))}
          </div>
        )}
      </div>
    </Settings>
  )
}

export default AdminManagement
